import { useState, useCallback, useRef } from 'react';

export type SortDirection = 'asc' | 'desc';

export interface UseTableSortResult<T extends string> {
  sortColumn: T | null;
  sortDirection: SortDirection;
  handleSort: (column: string) => void;
  resetSort: () => void;
}

export function useTableSort<T extends string>(
  defaultDirection: SortDirection = 'desc'
): UseTableSortResult<T> {
  const [sortColumn, setSortColumn] = useState<T | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultDirection);
  const sortColumnRef = useRef<T | null>(null);
  const sortDirectionRef = useRef<SortDirection>(defaultDirection);

  const handleSort = useCallback((column: string) => {
    const col = column as T;
    if (sortColumnRef.current === col) {
      if (sortDirectionRef.current !== defaultDirection) {
        // Third click: reset
        sortColumnRef.current = null;
        sortDirectionRef.current = defaultDirection;
        setSortColumn(null);
        setSortDirection(defaultDirection);
      } else {
        // Second click: flip direction
        const flipped = defaultDirection === 'asc' ? 'desc' : 'asc';
        sortDirectionRef.current = flipped;
        setSortDirection(flipped);
      }
    } else {
      // First click on a new column
      sortColumnRef.current = col;
      sortDirectionRef.current = defaultDirection;
      setSortColumn(col);
      setSortDirection(defaultDirection);
    }
  }, [defaultDirection]);

  const resetSort = useCallback(() => {
    setSortColumn(null);
    setSortDirection(defaultDirection);
  }, [defaultDirection]);

  return {
    sortColumn,
    sortDirection,
    handleSort,
    resetSort,
  };
}
